import { useEffect, useState } from 'react';
import {
  DEPLOYMENT_TYPES,
  LIFECYCLE_LABELS,
  LIFECYCLE_STATES,
  ORGANISATION_TYPES,
} from '@hospasense/contracts';
import { titleCase } from '../lib/format.js';
import { Button, Input, Modal, Select } from '../components/ui/index.js';

/* ---------------------------------------------------------------- columns */

export const ALL_COLUMNS = [
  'displayName',
  'legalName',
  'organisationType',
  'deploymentType',
  'jurisdiction',
  'lifecycleState',
  'tags',
  'owners',
  'primaryContact',
  'updatedAt',
] as const;
export type ColumnKey = (typeof ALL_COLUMNS)[number];

export const COLUMN_LABELS: Record<ColumnKey, string> = {
  displayName: 'Organisation',
  legalName: 'Legal name',
  organisationType: 'Type',
  deploymentType: 'Deployment',
  jurisdiction: 'Jurisdiction',
  lifecycleState: 'Lifecycle',
  tags: 'Tags',
  owners: 'Owners',
  primaryContact: 'Primary contact',
  updatedAt: 'Updated',
};

const DEFAULT_COLUMNS: ColumnKey[] = [
  'displayName',
  'organisationType',
  'deploymentType',
  'jurisdiction',
  'lifecycleState',
  'owners',
  'updatedAt',
];
const COLUMNS_KEY = 'hs-org-columns';

export function useColumnSelection(): [ColumnKey[], (next: ColumnKey[]) => void] {
  const [columns, setColumns] = useState<ColumnKey[]>(() => {
    try {
      const raw = localStorage.getItem(COLUMNS_KEY);
      if (!raw) return DEFAULT_COLUMNS;
      const parsed = JSON.parse(raw) as string[];
      const valid = parsed.filter((c): c is ColumnKey =>
        (ALL_COLUMNS as readonly string[]).includes(c),
      );
      return valid.length > 0 ? valid : DEFAULT_COLUMNS;
    } catch {
      return DEFAULT_COLUMNS;
    }
  });
  const update = (next: ColumnKey[]): void => {
    setColumns(next);
    try {
      localStorage.setItem(COLUMNS_KEY, JSON.stringify(next));
    } catch {
      // selection simply won't persist
    }
  };
  return [columns, update];
}

export function ColumnPicker({
  columns,
  onChange,
}: {
  columns: ColumnKey[];
  onChange: (next: ColumnKey[]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Columns
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Visible columns">
        <div className="grid grid-cols-2 gap-2">
          {ALL_COLUMNS.map((key) => {
            const checked = columns.includes(key);
            const locked = key === 'displayName';
            return (
              <label key={key} className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={locked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...columns, key]
                      : columns.filter((c) => c !== key);
                    onChange(ALL_COLUMNS.filter((c) => next.includes(c)));
                  }}
                  className="size-4 accent-(--primary)"
                />
                {COLUMN_LABELS[key]}
                {locked && <span className="text-xs text-text-muted">(always)</span>}
              </label>
            );
          })}
        </div>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------ saved views */

export interface SavedView {
  name: string;
  search: string; // URLSearchParams string
}
const VIEWS_KEY = 'hs-org-saved-views';

export function useSavedViews(): [SavedView[], (views: SavedView[]) => void] {
  const [views, setViews] = useState<SavedView[]>(() => {
    try {
      return (JSON.parse(localStorage.getItem(VIEWS_KEY) ?? '[]') as SavedView[]).slice(0, 20);
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEWS_KEY, JSON.stringify(views));
    } catch {
      // views simply won't persist
    }
  }, [views]);
  return [views, setViews];
}

export function SavedViews({
  current,
  onApply,
}: {
  current: URLSearchParams;
  onApply: (search: string) => void;
}) {
  const [views, setViews] = useSavedViews();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        label="Saved views"
        aria-label="Saved views"
        className="min-w-40"
        value=""
        onChange={(e) => {
          if (e.target.value) onApply(e.target.value);
        }}
      >
        <option value="">Apply a saved view…</option>
        {views.map((v) => (
          <option key={v.name} value={v.search}>
            {v.name}
          </option>
        ))}
      </Select>
      <div className="pt-5">
        <Button variant="outline" size="sm" onClick={() => setNaming(true)}>
          Save view
        </Button>
      </div>
      <Modal
        open={naming}
        onClose={() => setNaming(false)}
        title="Save current view"
        footer={
          <>
            <Button variant="outline" onClick={() => setNaming(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const trimmed = name.trim();
                if (!trimmed) return;
                setViews([
                  { name: trimmed, search: current.toString() },
                  ...views.filter((v) => v.name !== trimmed),
                ]);
                setName('');
                setNaming(false);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <Input
          label="View name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Live on-prem accounts"
          required
        />
      </Modal>
    </div>
  );
}

/* ---------------------------------------------------------------- filters */

export function OrgFilters({
  params,
  setParam,
}: {
  params: URLSearchParams;
  setParam: (key: string, value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
      <div className="col-span-2">
        <Input
          label="Search"
          type="search"
          placeholder="Name or legal name…"
          value={params.get('search') ?? ''}
          onChange={(e) => setParam('search', e.target.value)}
        />
      </div>
      <Select
        label="Lifecycle"
        value={params.get('lifecycleState') ?? ''}
        onChange={(e) => setParam('lifecycleState', e.target.value)}
      >
        <option value="">All states</option>
        {LIFECYCLE_STATES.map((s) => (
          <option key={s} value={s}>
            {LIFECYCLE_LABELS[s]}
          </option>
        ))}
      </Select>
      <Select
        label="Type"
        value={params.get('organisationType') ?? ''}
        onChange={(e) => setParam('organisationType', e.target.value)}
      >
        <option value="">All types</option>
        {ORGANISATION_TYPES.map((t) => (
          <option key={t} value={t}>
            {titleCase(t)}
          </option>
        ))}
      </Select>
      <Select
        label="Deployment"
        value={params.get('deploymentType') ?? ''}
        onChange={(e) => setParam('deploymentType', e.target.value)}
      >
        <option value="">All deployments</option>
        {DEPLOYMENT_TYPES.map((d) => (
          <option key={d} value={d}>
            {d === 'on_prem' ? 'On-prem' : 'Cloud'}
          </option>
        ))}
      </Select>
      <Select
        label="Attention"
        value={params.get('attention') ?? ''}
        onChange={(e) => setParam('attention', e.target.value)}
      >
        <option value="">Everything</option>
        <option value="stale">Stale records</option>
        <option value="missing_contacts">Missing contacts</option>
        <option value="unowned">No internal owner</option>
      </Select>
    </div>
  );
}
